import { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ToastData {
  id: string;
  icon?: React.ReactNode;
  title: string;
  message: string;
  actionUrl?: string;
  duration?: number;
}

type ToastListener = (toast: ToastData) => void;

let toastListener: ToastListener | null = null;

export function showToast(toast: Omit<ToastData, 'id'>): void {
  const id = crypto.randomUUID();
  toastListener?.({ ...toast, id });
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const navigate = useNavigate();

  // Register as the singleton listener
  useEffect(() => {
    toastListener = (toast: ToastData) => {
      setToasts((prev) => {
        // Max 3 visible
        const next = [toast, ...prev].slice(0, 3);
        return next;
      });
    };
    return () => {
      toastListener = null;
    };
  }, []);

  // Set up auto-dismiss timers
  useEffect(() => {
    toasts.forEach((toast) => {
      if (!timersRef.current.has(toast.id)) {
        const timer = setTimeout(() => {
          dismissToast(toast.id);
        }, toast.duration || 5000);
        timersRef.current.set(toast.id, timer);
      }
    });
  }, [toasts]);

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleClick = useCallback(
    (toast: ToastData) => {
      if (toast.actionUrl) {
        navigate(toast.actionUrl);
      }
      dismissToast(toast.id);
    },
    [navigate, dismissToast],
  );

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-[60] flex flex-col gap-2 w-80 md:w-96 pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto animate-in slide-in-from-right fade-in duration-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl rounded-xl p-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
          onClick={() => handleClick(toast)}
          role="alert"
        >
          {/* Icon */}
          {toast.icon && (
            <div className="flex-shrink-0 text-lg leading-none mt-0.5">
              {toast.icon}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {toast.title}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {toast.message}
            </p>
          </div>

          {/* Close */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              dismissToast(toast.id);
            }}
            className="flex-shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
